/**
 * Axios_Instance.ts
 * Shared axios instance for SK Catalog.
 * - withCredentials: true  → sends the platform's httpOnly auth cookies on
 *   every request (login is owned by skplatform-api; the cookie is host-only
 *   on localhost so it reaches both :3013 and :3001).
 * - A response interceptor transparently refreshes an expired access token
 *   once (POST /v1/auth/refresh) and replays the original request, so screens
 *   never see a spurious 401 mid-session.
 *
 * NOTE: The base URL is intentionally empty — each service uses the absolute
 * URLs defined in Api_Endpoints.ts.
 */

import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { Platform_Refresh_Token_Url } from './endpoints';
import { limitHitFrom, setLimitHit } from '../utils/limitHit';

const axiosInstance = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30_000,
});

/* ── Auto-trim string inputs ──────────────────────────────────────────────
 * Users often leave stray spaces (" 120 ", "Filter Coffee "). We trim every
 * string in the request body ONCE, centrally, so nothing is ever persisted
 * with leading/trailing whitespace. Sensitive / format-significant fields
 * (passwords, tokens, secrets, MFA codes) are left untouched.
 */
const NO_TRIM_KEYS = new Set([
  'password', 'new_password', 'current_password', 'old_password',
  'token', 'refresh_token', 'mfa_token', 'code', 'secret',
]);

function trimDeep(value: any, key?: string): any {
  if (typeof value === 'string') {
    return key && NO_TRIM_KEYS.has(key) ? value : value.trim();
  }
  if (Array.isArray(value)) return value.map((v) => trimDeep(v));
  // Only walk plain objects — never File/Blob/FormData/etc.
  if (value && typeof value === 'object' && (value.constructor === Object || value.constructor === undefined)) {
    const out: Record<string, any> = {};
    for (const k of Object.keys(value)) out[k] = trimDeep(value[k], k);
    return out;
  }
  return value;
}

/* ── Request interceptor ──────────────────────────────────────────────── */
axiosInstance.interceptors.request.use(
  (config) => {
    const d = config.data;
    const isPlain = d && typeof d === 'object'
      && !(typeof FormData !== 'undefined' && d instanceof FormData)
      && !(typeof Blob !== 'undefined' && d instanceof Blob);
    if (isPlain) config.data = trimDeep(d);
    return config;
  },
  (error) => Promise.reject(error),
);

/* ── Silent refresh (single-flight) ───────────────────────────────────── */
// Endpoints that must NOT trigger a refresh-retry: a 401 from these is terminal
// (bad creds / the refresh itself failing). `/me` is intentionally NOT here —
// on app boot an expired access token should silently refresh from the 30-day
// refresh cookie and retry, so a reload keeps the user signed in.
const NO_REFRESH = ['/auth/login', '/auth/signup', '/auth/refresh'];

let refreshInFlight: Promise<void> | null = null;

function refreshSession(): Promise<void> {
  // Single-flight: many parallel 401s share ONE refresh call.
  if (!refreshInFlight) {
    refreshInFlight = axiosInstance
      .post(Platform_Refresh_Token_Url)
      .then(() => undefined)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/* ── Response interceptor ─────────────────────────────────────────────── */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status;
    const url = original?.url ?? '';

    // A plan refusal (402/409 with an upgrade hint) also lights the console's banner.
    const hit = limitHitFrom(status, error.response?.data);
    if (hit) setLimitHit(hit);

    const refreshable =
      status === 401 &&
      original &&
      !original._retried &&
      !NO_REFRESH.some((p) => url.includes(p));

    if (refreshable) {
      try {
        await refreshSession();
        original._retried = true;
        return axiosInstance(original);
      } catch {
        // Refresh failed — fall through and surface the original 401 so the
        // app can route back to login. Error_Handler.ts extracts the message.
        return Promise.reject(error);
      }
    }

    // Pass error through — Error_Handler.ts will extract the backend message.
    return Promise.reject(error);
  },
);

export default axiosInstance;
